import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const FAQSection = ({ data }) => {
  const [active, setActive] = useState(0);

  if (!data || data.length === 0) return null;

  return (
    <section
      className="
        relative
        overflow-hidden
        bg-[var(--bg-primary)]
        py-4

        lg:py-8
      "
    >
      {/* ============================================================
          BACKGROUND GLOW - LEFT
      ============================================================ */}

      <div
        className="
          absolute
          -left-32
          top-20
          h-[260px]
          w-[260px]
          rounded-full
          bg-orange-500/10
          blur-[150px]

          md:h-[320px]
          md:w-[320px]
        "
      />

      {/* ============================================================
          BACKGROUND GLOW - RIGHT
      ============================================================ */}

      <div
        className="
          absolute
          right-0
          bottom-0
          h-[260px]
          w-[260px]
          rounded-full
          bg-orange-500/10
          blur-[150px]

          md:h-[320px]
          md:w-[320px]
        "
      />

      {/* ============================================================
          BACKGROUND GRID
      ============================================================ */}

      <div
        className="
          pointer-events-none
          absolute
          inset-0

          dark:bg-[linear-gradient(rgba(255,255,255,.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.03)_1px,transparent_1px)]

          bg-[linear-gradient(rgba(0,0,0,.035)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,.035)_1px,transparent_1px)]

          bg-[size:70px_70px]
        "
      />

      {/* ============================================================
          MAIN CONTAINER
      ============================================================ */}

      <div
        className="
          relative
          mx-auto
          max-w-7xl
          px-5

          sm:px-6

          lg:px-8
        "
      >
        {/* ==========================================================
            HEADING
        ========================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 35,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.6,
          }}
          className="
            mx-auto
            mb-12
            max-w-3xl
            text-center

            md:mb-16

            lg:mb-20
          "
        >
          {/* ========================================================
              BADGE
          ======================================================== */}

          <span
            className="
              inline-flex
              rounded-full
              border
              border-orange-500/20
              bg-orange-500/10
              px-4
              py-2
              text-[11px]
              font-semibold
              uppercase
              tracking-[2px]
              text-[var(--brand-primary)]

              sm:px-5
              sm:text-xs
              sm:tracking-[3px]
            "
          >
            Frequently Asked Questions
          </span>

          {/* ========================================================
              HEADING
          ======================================================== */}

          <h2
            className="
              mt-5
              text-3xl
              font-bold
              text-[var(--text-primary)]

              sm:text-4xl

              lg:mt-6
              lg:text-5xl
            "
          >
            Have Questions?
          </h2>

          {/* ========================================================
              DESCRIPTION
          ======================================================== */}

          <p
            className="
              mx-auto
              mt-5
              max-w-2xl
              text-base
              leading-7
              text-[var(--text-muted)]

              sm:text-lg
              sm:leading-8
            "
          >
            Find answers to the most commonly asked questions regarding
            this legal service and understand the registration process with
            complete clarity.
          </p>
        </motion.div>

        {/* ==========================================================
            FAQ LIST
        ========================================================== */}

        <div
          className="
            space-y-4

            sm:space-y-5

            lg:space-y-6
          "
        >
          {data.map((faq, index) => {
            const open = active === index;

            return (
              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 30,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.07,
                }}
                whileHover={{
                  y: -2,
                }}
                className="
                  group
                  overflow-hidden
                  rounded-2xl
                  border
                  border-[var(--border-primary)]
                  bg-[var(--card-bg-secondary)]

                  lg:rounded-3xl

                  transition-all
                  duration-300

                  hover:border-orange-500/30
                  hover:shadow-[0_15px_35px_rgba(255,136,0,.08)]
                "
              >
                {/* ==================================================
                    FAQ BUTTON
                ================================================== */}

                <button
                  type="button"
                  onClick={() => setActive(open ? null : index)}
                  className="
                    flex
                    w-full
                    items-center
                    justify-between
                    gap-4
                    px-5
                    py-5
                    text-left

                    sm:px-6
                    sm:py-6

                    lg:px-8
                    lg:py-7
                  "
                >
                  {/* ==================================================
                      QUESTION
                  ================================================== */}

                  <div
                    className="
                      flex-1
                      pr-2

                      sm:pr-4

                      lg:pr-6
                    "
                  >
                    <h3
                      className="
                        text-base
                        font-semibold
                        leading-7
                        text-[var(--text-primary)]

                        sm:text-lg

                        lg:text-xl
                      "
                    >
                      {faq.question}
                    </h3>
                  </div>

                  {/* ==================================================
                      PLUS BUTTON
                  ================================================== */}

                  <motion.div
                    animate={{
                      rotate: open ? 45 : 0,
                    }}
                    transition={{
                      duration: 0.3,
                    }}
                    className="
                      flex
                      h-10
                      w-10
                      shrink-0
                      items-center
                      justify-center
                      rounded-full
                      bg-[var(--brand-primary)]
                      text-white

                      sm:h-11
                      sm:w-11

                      lg:h-12
                      lg:w-12
                    "
                  >
                    <Plus
                      size={18}
                      className="
                        sm:h-5
                        sm:w-5
                      "
                    />
                  </motion.div>
                </button>

                {/* ==================================================
                    ANSWER
                ================================================== */}

                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{
                        height: 0,
                        opacity: 0,
                      }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                      }}
                      transition={{
                        duration: 0.3,
                      }}
                    >
                      <div
                        className="
                          border-t
                          border-[var(--border-primary)]
                          px-5
                          pt-4
                          pb-5

                          sm:px-6
                          sm:pt-5
                          sm:pb-6

                          lg:px-8
                          lg:pt-5
                          lg:pb-7
                        "
                      >
                        <p
                          className="
                            text-sm
                            leading-7
                            text-[var(--text-muted)]

                            sm:text-[15px]

                            lg:text-base
                            lg:leading-8
                          "
                        >
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;